/**
 * Funnel-stage classifier for the content funnel matrix (F14).
 *
 * Content routes are organized top-of-funnel (TOF, cold-audience discovery) →
 * mid-funnel (MOF, comparison/consideration) → bottom-of-funnel (BOF,
 * commercial-intent/conversion) per docs/plans/content-funnel-execution-
 * 2026-07-22.md. This module is a small **pure** function: it maps a
 * pathname's route prefix (locale-stripped) to that stage. The provider
 * (`posthog-provider.tsx`) calls it on each `$pageview` and, when it resolves,
 * sets `funnel_stage` — build a PostHog funnel insight
 * (TOF entry → MOF view → BOF view → signup) as a breakdown on that property.
 *
 * No PII, no network, no bundle weight beyond a static table — safe to import
 * anywhere on the request path. A pathname outside every mapped prefix (the
 * homepage, jobs feed/detail, auth, admin, …) returns `null` — the "core
 * product" surface, not content-funnel copy.
 */

export type FunnelStage = "tof" | "mof" | "bof";

/**
 * Route prefix (locale-stripped, leading slash) → funnel stage. Prefix match,
 * not exact-segment match, on purpose: `/panduan-remote/...` (the only
 * `panduan*` route today) must classify under the `panduan` TOF bucket
 * without a second, redundant entry. None of these prefixes is itself a
 * prefix of another entry, so match order does not matter.
 */
const FUNNEL_STAGE_ROUTES: ReadonlyArray<readonly [string, FunnelStage]> = [
	// ── TOF: cold-audience discovery / awareness content ──
	["/blog", "tof"],
	["/glossary", "tof"],
	["/panduan", "tof"],
	["/checklist", "tof"],
	["/insights", "tof"],
	["/digest", "tof"],
	["/press", "tof"],

	// ── MOF: comparison / consideration content ──
	["/landing", "mof"],
	["/alternatif", "mof"],
	["/banding", "mof"],
	["/resources", "mof"],
	["/gaji-vs", "mof"],
	["/salary-guide", "mof"],
	["/reports", "mof"],

	// ── BOF: commercial-intent / conversion surfaces ──
	["/pricing", "bof"],
	["/employers", "bof"],
	["/tools", "bof"],
	["/security", "bof"],
	["/developers", "bof"],
	["/jobs-apply", "bof"],
];

/** Strips a leading `/id` or `/en` locale segment, mirroring `src/lib/zones.ts`. */
function stripLocale(pathname: string): string {
	return pathname.replace(/^\/(id|en)(?=\/|$)/, "");
}

/**
 * Classifies a pathname into its content-funnel stage, or `null` when it
 * matches no mapped prefix (the caller should simply skip setting the
 * property in that case).
 *
 * @param pathname the route pathname (e.g. `usePathname()`'s value, or
 * `$pathname`), with or without a leading `/id`/`/en` locale segment.
 */
export function deriveFunnelStage(
	pathname: string | null | undefined,
): FunnelStage | null {
	if (!pathname) return null;
	const path = stripLocale(pathname);
	if (path.length === 0) return null;

	for (const [prefix, stage] of FUNNEL_STAGE_ROUTES) {
		if (path.startsWith(prefix)) return stage;
	}
	return null;
}
